import {Injectable} from '@angular/core';
import {Camera, CameraResultType, CameraSource, ImageOptions, PermissionStatus, Photo} from '@capacitor/camera';
import {ImageAsset} from '../../../Models/Shared/image-asset.interface';

@Injectable({
  providedIn: 'root'
})
export class CameraManagerService {

  cameraOptions: ImageOptions = {
    quality: 90,
    allowEditing: false,
    source: CameraSource.Prompt,
    resultType: CameraResultType.Base64,
    promptLabelCancel: 'Cancelar',
    promptLabelHeader: 'Seleccionar foto',
    promptLabelPhoto: 'Elegir de la galeria',
    promptLabelPicture: 'Hacer una foto',
  };


  /**
   * Obtener una foto desde la cámara o la galería
   *
   */
  public async getPhotoResponse(source: string = 'prompt'): Promise<ImageAsset | null> {

    try {

      const hasCameraPermission: boolean = await this.checkCameraPermissions();

      if (!hasCameraPermission) {
        return null;
      }

      switch (source) {
        case 'photos':
          this.cameraOptions.source = CameraSource.Photos;
          break;

        case 'camera':
          this.cameraOptions.source = CameraSource.Camera;
          break;

        case 'prompt':
          this.cameraOptions.source = CameraSource.Prompt;
          break;
      }

      const image: Photo = await Camera.getPhoto(this.cameraOptions);
      const imageResponse: Response = await this.base64ImageToBlob(image);
      const blob: Blob = await imageResponse.blob();

      return {
        original: image,
        blob: blob,
        url: URL.createObjectURL(blob),
        extension: `${image.format}`,
      };

    } catch (error) {

      console.error('Failed to capture photo:', error);
      return null;
    }
  }


  /**
   * Verificar y solicitar los permisos de la cámara
   *
   */
  public async checkCameraPermissions(): Promise<boolean> {
    const permissions: PermissionStatus = await Camera.checkPermissions();

    if (permissions.camera === 'granted') {
      return true;
    }

    if (permissions.camera === 'denied') {
      alert('Acceso a la cámara denegado. Habilítalo en la configuración del sistema');
      return false;
    }

    const request: PermissionStatus = await Camera.requestPermissions();
    return request.camera === 'granted';
  }


  /**
   * Crear imagen desde una URL
   *
   */
  public createFromUrl(url: string): ImageAsset {
    return {
      original: null,
      blob: null,
      url: url,
      extension: new URL(url).pathname.split('.').pop()?.toLowerCase() || 'jpg',
    };
  }


  /**
   * Convertir la imagen de base64 > blob
   *
   */
  private async base64ImageToBlob(image: Photo): Promise<Response> {
    return await fetch(`data:image/jpg;base64,${image.base64String}`);
  }

}
